import { axiosPlacement } from "./AxiosInstance.js"
import { getMe } from "./AuthService"

// Get current user from storage (source of truth updated on login/logout)
const getCurrentUserFromStorage = () => {
  try {
    const storedUser = localStorage.getItem('userDetails');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // Backend returns data wrapped in a data object sometimes, normalize it
      return parsed.data || parsed;
    }
  } catch (error) {
    console.error('Error parsing user details from storage:', error);
  }
  return null;
};

// Get comments by commentOnId (for specific entity) - UPDATED VERSION
export const getCommentsByEntity = async (commentOnId, type = 'jobprofiles') => {
  try {
    // console.log('🔍 Loading comments for:', { commentOnId, type }); // Debug log
    
    // Build query parameters to filter by specific entity ID
    const queryParams = new URLSearchParams({
      commentOnId: commentOnId,
      status: 'active',
      limit: 1000,
      type: type
    });
    
    const response = await axiosPlacement.get(`/comments?${queryParams.toString()}`);
    
    // console.log('📄 API Response:', response.data); // Debug log
    
    if (response && response.data) {
      // Handle the new response format: response.data.data is the actual array
      const commentsArray = response.data.data || (Array.isArray(response.data) ? response.data : []);
      
      // We don't need to filter again because the API already filtered by commentOnId and status
      return { 
        ...response, 
        data: commentsArray 
      };
    }
    
    return response;
  } catch (error) {
    console.error('❌ Fetch entity comments error:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch entity comments: ${error.response?.data?.message || error.message}`);
  }
};

// Create new comment with enhanced error handling - UPDATED VERSION
export const createComment = async (backendPayload) => {
  // console.log('📝 Creating comment with payload:', backendPayload); // Debug log
  
  const allowedFields = [
    "commentorId",
    "commentOnId", 
    "comment",
    "attachmentsPath",
    "status",
    "priority",
    "type",           
    "repliedTo",
    "createdById",
    "updatedById"
  ];

  const filteredPayload = Object.fromEntries(
    Object.entries(backendPayload).filter(([key]) => allowedFields.includes(key))
  );

  try {
    if (!filteredPayload.commentOnId || !filteredPayload.comment) {
      throw new Error('commentOnId and comment are required');
    }
    if (!filteredPayload.commentorId || !filteredPayload.createdById || !filteredPayload.updatedById) {
      const me = getCurrentUserFromStorage();
      const uid = me?.id || me?._id;
      if (uid) {
        if (!filteredPayload.commentorId) filteredPayload.commentorId = uid;
        if (!filteredPayload.createdById) filteredPayload.createdById = uid;
        if (!filteredPayload.updatedById) filteredPayload.updatedById = uid;
      }
    }
    const response = await axiosPlacement.post('/comments', filteredPayload);
    // console.log('✅ Comment created:', response.data); // Debug log
    return response.data;
  } catch (error) {
    console.error('❌ Comment creation failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to create comment: ${error.response?.data?.message || error.message}`);
  }
};

// Update comment
export const updateComment = async (id, backendPayload) => {
  const allowedFields = [
    "comment",
    "attachmentsPath",
    "status",
    "priority",
    "type",
    "updatedById"
  ];

  const filteredPayload = Object.fromEntries(
    Object.entries(backendPayload).filter(([key]) => allowedFields.includes(key))
  );

  try {
    const response = await axiosPlacement.put(`/comments/${id}`, filteredPayload);
    return response.data;
  } catch (error) {
    console.error('Comment update failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to update comment: ${error.response?.data?.message || error.message}`);
  }
};

// Delete comment
export const deleteComment = async (id) => {
  try {
    await axiosPlacement.delete(`/comments/${id}`);
    return id;
  } catch (error) {
    console.error('Comment deletion failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to delete comment: ${error.response?.data?.message || error.message}`);
  }
};

// UPDATED File upload function with user authentication
export const uploadFile = async (file) => {
  try {
    const currentUser = getCurrentUserFromStorage();
    const userId = currentUser?.id || currentUser?._id;

    if (!userId) {
      throw new Error('User authentication failed. Please login again.');
    }

    const formData = new FormData();
    formData.append('image', file); // Match backend field name expectation
    
    // Add required fields for backend validation with actual user ID
    formData.append('createdBy', userId);
    formData.append('updatedBy', userId);
    
    const response = await axiosPlacement.post('/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for large uploads
    });
    
    // console.log('Upload response:', response.data);
    
    // Return the response data with proper path handling
    if (response.data && !response.data.isError && response.data.data) {
      return {
        ...response.data.data,
        path: response.data.data.filePath || response.data.data.path // Ensure path is available
      };
    }
    
    // Fallback for different response formats
    return response.data;
  } catch (error) {
    console.error('File upload failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    // Better error handling for specific multer errors
    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || 'File upload failed';
      if (errorMessage.includes('Unexpected field')) {
        throw new Error('File upload configuration error. Please try again.');
      }
      if (errorMessage.includes('File too large')) {
        throw new Error('File size is too large. Please select a smaller file.');
      }
      if (errorMessage.includes('Only image files')) {
        throw new Error('Only image files are allowed.');
      }
      throw new Error(errorMessage);
    }
    
    throw new Error(`Failed to upload file: ${error.response?.data?.message || error.message}`);
  }
};

// UPDATED Get uploaded files/images with user-based filtering
export const getUploadedFiles = async () => {
  try {
    const currentUser = getCurrentUserFromStorage();

    if (!currentUser) {
      throw new Error('User authentication failed. Please login again.');
    }

    const response = await axiosPlacement.get('/images');
    
    // console.log('Images response:', response.data);
    
    // Handle the backend response format properly
    if (response.data && !response.data.isError) {
      let imagesArray = [];
      
      // Check if data is nested in response.data.data
      if (response.data.data) {
        imagesArray = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      } else if (Array.isArray(response.data)) {
        imagesArray = response.data;
      }
      
      // Filter images based on user permissions
      let filteredImages = imagesArray;
      
      // If user is not superadmin, only show their own images
      if (!currentUser.isSuperAdmin) {
        filteredImages = imagesArray.filter(image => 
          image.createdBy === currentUser.id || 
          image.updatedBy === currentUser.id
        );
      }
      // If superadmin, show all images (no filtering needed)
      
      // Map the response to ensure consistent format with path property
      const formattedImages = filteredImages.map(image => ({
        ...image,
        path: image.filePath || image.path // Ensure path property exists for frontend use
      }));
      
      // console.log('Filtered and formatted images:', formattedImages);
      
      return formattedImages;
    }
    
    return [];
  } catch (error) {
    console.error('Fetch uploaded files failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch uploaded files: ${error.response?.data?.message || error.message}`);
  }
};

// NEW Function to check if image is used in any comments
export const checkImageUsageInComments = async (imagePath) => {
  try {
    // Get all comments to check for image usage
    const response = await axiosPlacement.get('/comments?limit=50000&status=active');
    
    let commentsArray = [];
    if (response.data) {
      commentsArray = Array.isArray(response.data) ? response.data : response.data.data || [];
    }
    
    // Filter active comments that are not deleted
    const activeComments = commentsArray.filter(comment => 
      comment.status === 'active' && !comment.isDeleted
    );
    
    // Check if any comment uses this image path
    const usageComments = activeComments.filter(comment => 
      comment.attachmentsPath === imagePath
    );
    
    // console.log('🔍 Image usage check:', { imagePath, usageCount: usageComments.length });
    
    return {
      isUsed: usageComments.length > 0,
      usageCount: usageComments.length,
      comments: usageComments
    };
  } catch (error) {
    console.error('Check image usage failed:', error);
    throw new Error(`Failed to check image usage: ${error.response?.data?.message || error.message}`);
  }
};

// NEW Function to delete uploaded image
export const deleteUploadedImage = async (imageId, imagePath) => {
  try {
    const currentUser = getCurrentUserFromStorage();

    if (!currentUser) {
      throw new Error('User authentication failed. Please login again.');
    }

    // First check if image is used in any comments
    const usageCheck = await checkImageUsageInComments(imagePath);
    
    if (usageCheck.isUsed) {
      throw new Error(
        `This image cannot be deleted because it is currently being used in ${usageCheck.usageCount} comment(s). Please remove it from all comments first.`
      );
    }

    // Get image details to check ownership (if needed)
    const imageResponse = await axiosPlacement.get(`/images/${imageId}`);
    const imageData = imageResponse.data?.data || imageResponse.data;

    // Check if user has permission to delete this image
    if (!currentUser.isSuperAdmin) {
      if (imageData.createdBy !== currentUser.id && imageData.updatedBy !== currentUser.id) {
        throw new Error('You do not have permission to delete this image.');
      }
    }

    // Proceed with deletion
    const response = await axiosPlacement.delete(`/images/${imageId}`);
    
    // console.log('✅ Image deleted successfully:', imageId);
    
    return {
      success: true,
      imageId,
      message: 'Image deleted successfully'
    };
  } catch (error) {
    console.error('Image deletion failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    // Re-throw custom errors (like usage check failures)
    if (error.message.includes('cannot be deleted because') || 
        error.message.includes('do not have permission')) {
      throw error;
    }
    
    throw new Error(`Failed to delete image: ${error.response?.data?.message || error.message}`);
  }
};

// Get current user info
export const getCurrentUser = async () => {
  try {
    const currentUser = getCurrentUserFromStorage();
    return { data: currentUser };
  } catch (error) {
    console.error('Get current user failed:', error);
    throw new Error(`Failed to get current user: ${error.message}`);
  }
};

// Get all comments with retry logic
export const getComments = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add filters to query parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const queryString = queryParams.toString();
    const url = queryString ? `/comments?${queryString}` : '/comments';
    
    const response = await axiosPlacement.get(url);
    return response.data;
  } catch (error) {
    console.error('Fetch comments error:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch comments: ${error.response?.data?.message || error.message}`);
  }
};

// Get comment by ID
export const getCommentById = async (id) => {
  try {
    const response = await axiosPlacement.get(`/comments/${id}`);
    return response.data;
  } catch (error) {
    console.error('Fetch comment error:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch comment: ${error.response?.data?.message || error.message}`);
  }
};

// Get replies for a specific comment
export const getCommentReplies = async (parentCommentId) => {
  try {
    const response = await axiosPlacement.get(`/comments?repliedTo=${parentCommentId}&status=active&limit=50000`);
    return response.data;
  } catch (error) {
    console.error('Fetch comment replies error:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch comment replies: ${error.response?.data?.message || error.message}`);
  }
};
